import { Component, ComponentFactoryResolver, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Models } from '@ibexfusion/models';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { SnackbarService } from '@services/snackbar/snackbar.service';
import * as _ from 'lodash';
import { FinishVotingDialogComponent } from './finish-voting-dialog/finish-voting-dialog.component';
import { GroupService } from '@services/groups/group.service';
import { GroupHasRoleInOrganisationService } from '@services/groups/group-has-role-in-organisation.service';
import { MessengerService } from '@services/messenger/messenger.service';
import { GroupHasSecuredAppService } from '@services/groups/group-has-secured-app.service';
import { DialogService } from '@services/dialog/dialog.service';
import { GroupSelectionComponent } from '../../../../../components/popUpSelects/group-selection/group-selection.component';

@Component({
    selector: 'voting',
    templateUrl: './voting.component.html',
    styleUrls: ['./voting.component.scss'],
    providers: [GroupService, GroupHasRoleInOrganisationService, GroupHasSecuredAppService],
    standalone: false
})

export class VotingComponent implements OnInit {
  @Input() dynamicContent: Models.DynamicContent;
  @Input() parent: Models.DynamicContentEntrance;
  @Input() create: any;
  @Output() finish = new EventEmitter();

  public tableData = null;
  public columns = [{
    prop: 'name', translationString: 'general.name',
    valueTransformer: (val) => (val || '').startsWith('pref_') ? val.split('_').splice(2, val.split('_').length - 1).join('_') : val
  }];
  readonly separatorKeysCodes = [ENTER, COMMA] as const;

  headerButtons = [{
    icon: 'add', tooltipTranslation: 'dynamicContent.votingAddGroup', callback: () => {
      return this.addGroup()
    }
  }];

  public form: UntypedFormGroup = new UntypedFormGroup({
    textContent: new UntypedFormControl('', Validators.compose([Validators.required, Validators.maxLength(180)])),
    type: new UntypedFormControl('', Validators.required),
    answers: new UntypedFormControl([]),
    allowedGroups: new UntypedFormControl([]),
    scaleMin: new UntypedFormControl(0),
    scaleMax: new UntypedFormControl(0),
    scaleStep: new UntypedFormControl(0),
    scaleIcon: new UntypedFormControl('', Validators.required),
    showIntermediateResult: new UntypedFormControl(false)
  });

  types = ['singleChoice', 'multipleChoice', 'scale'];
  scaleIconType = ['star', 'heart', 'numeric'];
  addOnBlur = true;
  answers = [];

  isMain = false;

  constructor(
    private messengerService: MessengerService, private groupService: GroupService, private ghrioService: GroupHasRoleInOrganisationService,
    private ghsaService: GroupHasSecuredAppService, private router: Router, private route: ActivatedRoute, private componentFactoryResolver: ComponentFactoryResolver,
    private snackbar: SnackbarService, private dialogService: DialogService, private dialog: MatDialog
  ) {
  }

  ngOnInit() {
    this.isMain = this.parent instanceof Models.DynamicContentEntrance;
    this.form.valueChanges.subscribe(res => {
      this.dynamicContent.content = Object.assign(this.dynamicContent.content, res);
    });
    if (this.dynamicContent.title) {
      this.form.patchValue(this.dynamicContent.content);
      this.answers = this.dynamicContent.content.answers;
    }
    this.initTableData();
  }

  finishVoting() {
    const dialogRef = this.dialog.open(FinishVotingDialogComponent, { width: '400px', panelClass: 'finish-voting-modal' });
    dialogRef.afterClosed().toPromise().then(result => {
      if (result && result.finished) {
        this.dynamicContent.content = Object.assign(this.dynamicContent.content, result);
        this.finish.emit(this.dynamicContent);
      }
    });
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value) {
      this.answers.push(value.slice(0, 80));
    }
    event.chipInput!.clear();
    this.form.patchValue({ answers: this.answers });
  }

  remove(answer): void {
    const index = this.answers.indexOf(answer);
    if (index >= 0) {
      this.answers.splice(index, 1);
    }
    this.form.patchValue({ answers: this.answers });
  }

  removeGroup(event) {
    this.tableData.splice(event.index, 1);
    this.form.patchValue({ allowedGroups: _.map(this.tableData, 'GroupID') });
    this.dynamicContent.content.allowedGroups = _.map(this.tableData, 'GroupID');
    this.initTableData();
  }

  initTableData() {
    if (!this.dynamicContent.content.allowedGroups || this.dynamicContent.content.allowedGroups.length === 0) {
      this.tableData = [];
      return;
    }
    this.groupService.find(0, 100, { GroupID: { $in: this.dynamicContent.content.allowedGroups ? this.dynamicContent.content.allowedGroups : [] } }).then(result => {
      if (!this.dynamicContent.content.allowedGroups) {
        result.data = [];
      }
      this.tableData = result.data;
    });
  }

  addGroup() {
    let groupIds = [];
    return this.ghrioService.find(0, 100, {
      roleName: 'securedContent',
      OrganisationID: this.messengerService.$activeOrganisation.getValue().OrganisationID
    }).then(groups => {
      groupIds = _.map(groups.data, 'GroupID');
      if (groupIds.length === 0) {
        return;
      }
      return this.ghsaService.find(0, 100, {
        Groups_GroupID: { $in: groupIds }
      }).then(securedApps => {
        groupIds = _.uniq(_.map(securedApps.data, 'Groups_GroupID'));
        if (groupIds.length === 0) {
          return;
        }
        const dialogRef = this.dialog.open(GroupSelectionComponent, {
          data: {
            ProfileID: this.route.snapshot.params['frontProfileID'],
            params: { GroupID: { $in: groupIds } },
            assignedGroupIds: this.dynamicContent.content.allowedGroups && this.dynamicContent.content.allowedGroups.length ? this.dynamicContent.content.allowedGroups : []
          },
          width: '800px'
        });
        dialogRef.afterClosed().subscribe(result => {
          if (!result || !result.all) {
            return;
          }
          this.form.patchValue({ allowedGroups: result.all });
          this.dynamicContent.content.allowedGroups = result.all;
          this.initTableData();
        });
      })
    });
  }
}
